/**
 * A project's hosted memory: what is in it by kind and repository, and a search over it that
 * answers only what the signed-in person may read, with each answer's label and source.
 *
 * List groups (Layout.tsx): what it holds, Ask (the question, then each answer as a row), what waits
 * for review (Accept at the end of each, Reject in its menu), the changes (Ask as it was at the end of
 * each), and what Microsoft 365 answered.
 */

import { memoryProposalTitle, type MemoryAnswer, type MemoryCapabilities, type MemoryItemKind, type MemoryProposal, type MemoryRevision, type Microsoft365Answer } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';
import { Database } from '@gravity-ui/icons';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { isCloud } from '../edition.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { Button, ChoiceSelect, ConfirmDialog, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, StatusMark, Tick } from './ui.tsx';
import { VIEW_TONES, type ArtTone } from './ui/Art.tsx';
import { RowMenu } from './ui/RowMenu.tsx';
import { Markdown } from './Markdown.tsx';

const KIND_TEXT: Record<MemoryItemKind, string> = { node: 'Concepts', edge: 'Relations', community: 'Groups', note: 'Notes', lesson: 'Lessons', decision: 'Decisions', artifact: 'Artifacts', knowledge: 'Knowledge' };
const SENSITIVITY_TEXT: Record<string, string> = { public: 'Public', internal: 'Internal', confidential: 'Confidential', restricted: 'Restricted' };

/** A project's memory, or the organisation's own index when `projectId` is left out. */
export function ProjectMemoryPanel({ projectId, projectNames }: { projectId?: string; projectNames?: Map<string, string> }) {
  const tone = projectId ? VIEW_TONES.projects! : VIEW_TONES.memory!;
  const status = useLoad(() => (projectId ? routes.memoryStatus(projectId) : routes.orgMemoryStatus()), [projectId], 30_000);
  const [question, setQuestion] = useState('');
  const [scope, setScope] = useState<'all' | 'repo' | 'knowledge'>('all');
  const [mine, setMine] = useState(false);
  const [asked, setAsked] = useState<{ question: string; scope: 'all' | 'repo' | 'knowledge'; mine: boolean }>();
  // "As it was on": a revision picked from Changes; Ask then reads the memory as it stood then (by words).
  const [then, setThen] = useState<MemoryRevision>();
  const asOf = then ? { asOf: { seq: then.seq } } : {};
  const answers = useLoad(
    () =>
      asked
        ? projectId
          ? routes.memoryQuery(projectId, { question: asked.question, scope: asked.scope, limit: 20, ...asOf })
          : routes.orgMemoryQuery({ question: asked.question, scope: asked.scope, limit: 20, ...(asked.mine && !then ? { projects: 'mine' } : {}), ...asOf })
        : Promise.resolve(undefined),
    [projectId, asked?.question, asked?.scope, asked?.mine, then?.seq],
  );
  const history = useLoad(() => routes.memoryRevisions(projectId).catch(() => undefined), [projectId, status.data?.seq]);
  // Microsoft 365, when an administrator has switched it on: the same question, asked for this person, kept as one conversation.
  const grounding = useLoad(() => (isCloud() ? Promise.resolve(undefined) : routes.microsoft365Grounding().catch(() => undefined)), []);
  const [conversation, setConversation] = useState<string>();
  const m365 = useLoad(
    () =>
      asked && grounding.data?.enabled
        ? routes.askMicrosoft365({ question: asked.question, ...(conversation ? { conversationId: conversation } : {}), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }).then((a) => (setConversation(a.conversationId), a))
        : Promise.resolve(undefined),
    [asked?.question, asked?.scope, asked?.mine, grounding.data?.enabled],
  );
  const s = status.data;
  const mirrored = s && 'mirrored' in s ? (s as { mirrored: Array<{ projectId: string; name: string; scope: string; items?: number; hidden?: number }> }).mirrored : undefined;
  const kinds = s ? (Object.keys(KIND_TEXT) as MemoryItemKind[]).filter((k) => s.byKind[k]) : [];
  return (
    <>
      <ListGroup
        title="What it holds"
        count={s?.items}
        description={projectId ? 'What the project’s workbenches have extracted and written back, held here as the index of record.' : 'Handbooks, standards and decisions filed here, and what projects promote or mirror into it.'}
        empty={
          s && s.items === 0 && !s.hidden ? (
            <Empty tone={tone} icon={<Database />} title="Nothing in memory yet" body={projectId ? 'A member’s workbench sends what it extracts from each repository, and notes, lessons and decisions written back from sessions.' : 'Roles granted memory:write file the organisation’s knowledge here, and a project’s owners promote or mirror its memory.'} />
          ) : undefined
        }
      >
        {s && s.items === 0 && s.hidden ? <ListBlock className="text-sm text-muted">{s.hidden} {s.hidden === 1 ? 'item is' : 'items are'} held here that you are not cleared to read.</ListBlock> : null}
        {!s ? <ListBlock className="text-sm text-muted">{status.error ? <ErrorNote error={status.error} /> : 'Reading the memory…'}</ListBlock> : null}
        {s && s.items > 0 ? (
          <ListBlock className="grid gap-4 text-sm @[35rem]:grid-cols-[minmax(0,18rem)_minmax(0,28rem)] @[35rem]:gap-x-16">
            <div>
              <div className="mb-1 text-xs text-muted">By kind</div>
              <ul className="grid gap-1">
                {kinds.map((k) => (
                  <li key={k} className="flex justify-between gap-4">
                    <span>{KIND_TEXT[k]}</span>
                    <span className="tabular-nums text-muted">{s.byKind[k]}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="mb-1 text-xs text-muted">{projectId ? 'By repository' : 'By source'}</div>
              <ul className="grid gap-1">
                {s.roots.map((r) => (
                  <li key={r.rootId} className="flex justify-between gap-4">
                    <span className="min-w-0 truncate">
                      {r.rootId ? (r.name ?? r.rootId) : projectId ? 'Project-level' : 'Filed directly'}
                      {r.name && r.name !== r.rootId && <span className="mono text-xs text-muted"> {r.rootId}</span>}
                      {r.lastIngestAt && <span className="text-muted"> · {r.complete === false ? 'still arriving' : `complete, updated ${timeAgo(r.lastIngestAt)}`}</span>}
                    </span>
                    <span className="tabular-nums text-muted">{r.items}</span>
                  </li>
                ))}
              </ul>
            </div>
          </ListBlock>
        ) : null}
        {s && s.items > 0 ? <Meaning capabilities={s.capabilities} off={s.meaning} /> : null}
        {mirrored?.map((m) => <ListRow key={m.projectId} title={`Mirrored from ${projectNames?.get(m.projectId) ?? m.name}`} value={`${m.scope === 'all' ? 'Everything' : 'Notes, lessons and decisions'} · ${m.items ?? 0} copied${m.hidden ? ` (${m.hidden} you cannot read)` : ''}`} />)}
        {s && status.error ? (
          <ListBlock>
            <ErrorNote error={status.error} />
          </ListBlock>
        ) : null}
      </ListGroup>
      <ListGroup
        title={then ? `Ask the memory as it was on ${whenText(then.at)}` : 'Ask the memory'}
        description={then ? 'Found by words, from what the memory said then, read at your clearance today.' : 'Answers are limited to what you are cleared to read; every search is audited by label.'}
        actions={
          then ? (
            <Button size="sm" variant="ghost" onPress={() => setThen(undefined)}>
              Back to now
            </Button>
          ) : undefined
        }
      >
        <ListBlock>
          <form
            className="grid gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (question.trim()) setAsked({ question: question.trim(), scope, mine });
            }}
          >
            <div className="flex flex-wrap gap-2">
              <input className={`${inputClass} min-w-48 flex-1`} aria-label="Question" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="How does billing talk to the ledger?" />
              <ChoiceSelect label="Scope" value={scope} options={{ all: 'Everything', repo: 'Repositories', knowledge: 'Notes and decisions' }} onChange={setScope} />
              <Button size="sm" type="submit" variant="primary" isDisabled={!question.trim()}>
                Search
              </Button>
            </div>
            {!projectId && !then && (
              <Tick checked={mine} onChange={setMine}>
                Also search the projects I am on
              </Tick>
            )}
            <ErrorNote error={answers.error} />
          </form>
        </ListBlock>
        {answers.data && answers.data.answers.length === 0 ? (
          <ListBlock className="text-sm text-muted">
            Nothing you can read matches.{answers.data.itemsFiltered ? ` ${answers.data.itemsFiltered} ${answers.data.itemsFiltered === 1 ? 'item was' : 'items were'} kept from you: above your clearance, or in a compartment you do not hold.` : ''}
          </ListBlock>
        ) : null}
        {answers.data?.answers.map((a: MemoryAnswer) => (
          <ListRow
            key={a.item.id}
            title={
              <>
                {!projectId && a.item.projectId ? <span className="text-muted">From {projectNames?.get(a.item.projectId) ?? a.item.projectId}: </span> : null}
                {a.item.name}
              </>
            }
            description={
              <>
                <span className="block">
                  {KIND_TEXT[a.item.kind]} · {SENSITIVITY_TEXT[a.item.label.sensitivity] ?? a.item.label.sensitivity}
                  {a.item.label.compartments.length ? ` · ${a.item.label.compartments.join(', ')}` : ''}
                </span>
                <Markdown compact breaks className="mt-1 text-foreground" text={a.text} />
                <span className="mt-1 block">{a.sources.map((src) => `${src.rootId ? `${src.rootId}: ` : ''}${src.source}${src.commit ? ` @ ${src.commit.slice(0, 8)}` : ''}`).join(' · ')}</span>
              </>
            }
          />
        ))}
        {answers.data && answers.data.itemsFiltered > 0 && answers.data.answers.length > 0 ? (
          <ListBlock className="text-xs text-muted">
            {answers.data.itemsFiltered} more {answers.data.itemsFiltered === 1 ? 'item was' : 'items were'} kept from you: above your clearance, or in a compartment you do not hold.
          </ListBlock>
        ) : null}
      </ListGroup>
      <WaitingForReview projectId={projectId} tone={tone} seq={status.data?.seq} onDecided={() => (status.reload(), history.reload())} />
      {history.data && history.data.revisions.length > 0 && (
        <ListGroup title="Changes" count={history.data.revisions.length} description="What changed in this memory, newest first, counting only what you may read.">
          {history.data.revisions.map((r) => (
            <ListRow
              key={r.seq}
              title={revisionSentence(r, status.data?.roots)}
              description={whenText(r.at)}
              status={then?.seq === r.seq ? <StatusMark state="online" label="Asking as it was" /> : undefined}
              compactControl
              control={
                <Button size="sm" variant="ghost" onPress={() => setThen(then?.seq === r.seq ? undefined : r)}>
                  {then?.seq === r.seq ? 'Back to now' : 'Ask as it was'}
                </Button>
              }
            />
          ))}
        </ListGroup>
      )}
      {asked && grounding.data?.enabled && <Microsoft365Section answer={m365.data} loading={m365.loading} error={m365.error} />}
    </>
  );
}

/** What Microsoft 365 Copilot answered for this person, beside the organisation's own memory and never mixed into it. */
function Microsoft365Section({ answer, loading, error }: { answer?: Microsoft365Answer; loading: boolean; error?: string }) {
  const sources = answer?.citations.filter((c) => c.kind === 'citation') ?? [];
  const named = answer?.citations.filter((c) => c.kind === 'annotation') ?? [];
  return (
    <ListGroup title="From Microsoft 365" description="Answered by Microsoft 365 Copilot for you, from what you may see there. Nothing is copied into the memory. Each question costs Copilot Credits.">
      <ListBlock className="grid gap-2 text-sm">
        <ErrorNote error={error ? problemText(error) : undefined} />
        {loading && !answer ? <p className="text-muted">Asking Microsoft 365…</p> : null}
        {answer ? (
          <>
            <Markdown breaks text={answer.text} fallback="Microsoft 365 had nothing to say." />
            {sources.length > 0 && (
              <div className="text-xs text-muted">
                Sources:{' '}
                {sources.map((c, i) => (
                  <span key={c.url}>
                    {i > 0 ? ' · ' : ''}
                    <a href={c.url} target="_blank" rel="noreferrer" className="hover:text-foreground hover:underline">
                      {c.title}
                    </a>
                  </span>
                ))}
              </div>
            )}
            {named.length > 0 && (
              <div className="text-xs text-muted">
                Open:{' '}
                {named.map((c, i) => (
                  <span key={c.url}>
                    {i > 0 ? ' · ' : ''}
                    <a href={c.url} target="_blank" rel="noreferrer" className="hover:text-foreground hover:underline">
                      {c.title}
                    </a>
                  </span>
                ))}
              </div>
            )}
            {answer.sensitivity ? <div className="text-xs text-muted">Sensitivity, as Microsoft labels it: {answer.sensitivity}</div> : null}
          </>
        ) : null}
      </ListBlock>
    </ListGroup>
  );
}

/**
 * Whether this memory is found by meaning as well as by words (D326), in a row: workbenches with a model on the
 * machine send each item's vector, and how many items have one. The console's own questions are found by words.
 */
function Meaning({ capabilities, off }: { capabilities?: MemoryCapabilities; off?: 'index-has-none' | 'off' }) {
  const e = capabilities?.embeddings;
  if (!e && !off) return null;
  return e ? (
    <ListRow
      title="Found by meaning"
      description={
        <>
          <span className="tabular-nums">{e.current.toLocaleString()}</span> of <span className="tabular-nums">{e.items.toLocaleString()}</span> items ready, sent by workbenches that have a model for it on the machine.
        </>
      }
      status={<StatusMark state={e.items && e.current >= e.items ? 'connected' : 'working'} label={e.items && e.current >= e.items ? 'Ready' : 'Filling in'} />}
    />
  ) : (
    <ListRow title="Found by words only" description={off === 'off' ? 'The organisation’s policy turns finding memory by meaning off.' : 'The database has no index for meaning yet (pgvector).'} status={<StatusMark state="idle" label="Words only" />} />
  );
}

const whenText = (iso: string) => dateTime(iso, { date: 'medium', year: false });

/** A revision in words: "billing-api read again at 3f2a91c: 214 changed, 12 new, 3 gone". */
function revisionSentence(r: MemoryRevision, roots?: Array<{ rootId: string; name?: string }>): string {
  const total = (c: Partial<Record<string, number>>) => Object.values(c).reduce<number>((n, x) => n + (x ?? 0), 0);
  const added = total(r.counts.added);
  const changed = total(r.counts.changed);
  const removed = total(r.counts.removed);
  const counts = [changed ? `${changed.toLocaleString()} changed` : '', added ? `${added.toLocaleString()} new` : '', removed ? `${removed.toLocaleString()} gone` : ''].filter(Boolean).join(', ');
  const where = r.rootId ? (roots?.find((x) => x.rootId === r.rootId)?.name ?? r.rootId) : 'project-level items';
  switch (r.kind) {
    case 'extraction':
      return `${where} read again${r.commit ? ` at ${r.commit.slice(0, 7)}` : ''}: ${counts}`;
    case 'write':
      return added ? `${added === 1 ? 'A note, lesson or decision' : `${added} notes, lessons or decisions`} written` : `A note, lesson or decision rewritten`;
    case 'relabel':
      return 'An item relabelled';
    case 'removal':
      return `${removed === 1 ? 'An item' : `${removed} items`} removed for good`;
    case 'promotion':
      return `Promoted from a project: ${counts}`;
    case 'mirror':
      return `Mirrored from a project: ${counts}`;
    default:
      return counts;
  }
}

/**
 * Agents' writes waiting for review (D327), oldest first: each says what it holds and who proposed it, and a person
 * who may read it accepts or rejects it here as well as from the Inbox. Nothing in it is in the memory until accepted.
 */
function WaitingForReview({ projectId, tone: _tone, seq, onDecided }: { projectId?: string; tone: ArtTone; seq?: number; onDecided: () => void }) {
  const waiting = useLoad(() => routes.memoryProposals(projectId).catch(() => undefined), [projectId, seq]);
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [rejecting, setRejecting] = useState<MemoryProposal>();
  const [reason, setReason] = useState('');
  const list = [...(waiting.data?.proposals ?? [])].sort((a, b) => a.proposedAt.localeCompare(b.proposedAt));
  if (!list.length) return null;
  const decide = async (p: MemoryProposal, decision: 'accept' | 'reject', why?: string) => {
    setBusy(p.id);
    setError(undefined);
    try {
      await routes.decideMemoryProposal(projectId, p.id, decision, why);
      waiting.reload();
      onDecided();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <>
    <ListGroup title="Waiting for review" count={list.length} description="What agents want to add to this memory. Accepting adds it as written; nobody reads it until then.">
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
      {list.map((p) => (
        <ListRow
          key={p.id}
          title={sentence(memoryProposalTitle(p))}
          description={
            <>
              <span className="block">
                {whenText(p.proposedAt)} · {p.reviewBy === 'manager' ? "the team's operations manager reviews it" : 'a person reviews it'}
              </span>
              {p.items
                .filter((i) => i.state === 'proposed')
                .map((i) => (
                  <span key={i.id} className="block">
                    <span className="text-foreground">{i.data.title}</span> · {KIND_TEXT[i.kind].replace(/s$/, '').toLowerCase()}
                    {i.key && i.baseVersion ? ', replaces the current one' : ''}
                  </span>
                ))}
            </>
          }
          compactControl
          control={
            <>
              <Button size="sm" variant="secondary" isPending={busy === p.id} isDisabled={Boolean(busy)} onPress={() => void decide(p, 'accept')}>
                Accept
              </Button>
              <RowMenu label={`More for ${memoryProposalTitle(p)}`} items={[{ id: 'reject', label: 'Reject…', danger: true, disabled: Boolean(busy) }]} onAction={() => (setReason(''), setRejecting(p))} />
            </>
          }
        />
      ))}
    </ListGroup>
      <ConfirmDialog
        isOpen={Boolean(rejecting)}
        title="Keep this out of the memory?"
        confirmLabel="Reject"
        danger
        onCancel={() => setRejecting(undefined)}
        onConfirm={async () => {
          if (rejecting) await decide(rejecting, 'reject', reason.trim() || 'Rejected from the Memory page, with no reason given');
          setRejecting(undefined);
        }}
      >
        <p className="text-sm text-muted">Say why: the agents that proposed it are told, so they do not propose it again.</p>
        <input className={inputClass} aria-label="Why it is rejected" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="A step of this run, not something to remember" autoFocus />
      </ConfirmDialog>
    </>
  );
}

const sentence = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
